import { ensureEarthEngineReady } from '../../services/earth-engine';
import { Actions as Acquisition } from '../../store/ducks/acquisition';
import { OverlayHelper } from '../../app/components/intertidal/IntertidalZoneDefiner';
import { aoiGeometry } from '../../common/aoi';

export const SESSION_EXPORT_VERSION = 1;
export const SESSION_EXPORT_MODULE = 'intertidal';

const PENDING_INTERTIDAL_STORAGE_KEY = 'intertidalSessionImportWaterlines';

/**
 * @function buildSessionExport
 * @description Bundles everything needed to redraw a completed Intertidal waterline analysis
 * without re-running the wizard. The waterlines themselves (`waterlineFeatures`) are already-
 * evaluated GeoJSON — like Shoreline, and unlike Bathymetry/CSqueeze's rasters, they're frozen and
 * replayed with zero Earth Engine calls. The AWEI/Kriging pipeline is NOT replayed by this first
 * pass (it needs live `ee.Image`s cached only in `IntertidalZoneDefiner`'s `OverlayHelper` static
 * class, populated only by actually running step 4) — "Generate Kriging" after an import behaves
 * the same as it would on a fresh page with no images loaded yet.
 * @param {Object} params
 * @param {Array<Array<Number>>} params.coordinates - AOI coordinates, `state.acquisition.coordinates`.
 * @param {Array<Object>} params.availableDates - `state.acquisition.availableDates`, the final
 * selected image list (read by `ImageChooserCard`/`LoadedImagesAccordion` on the result page).
 * @param {Array<Object>} params.waterlineLayers - `ProcessingIntertidalPage`'s own `waterlineLayers`
 * local state — one entry per processed image (date/color/tideValue/visible/feature offsets).
 * @param {Object} params.evaluatedEE - `result.jsx`'s module-level `evaluatedEE` — the accumulated
 * `{type: 'FeatureCollection', features}` every layer's polylines were sliced from.
 * Also captures `OverlayHelper.zoneIndex`/`.waterlineIndex` — the spectral index (AWEI/NDWI/MNDWI/
 * MNDWI-sharpened) picked in step 4 for zone identification and coastline detection respectively
 * — so a re-imported session shows the same index in the map layer label and in step 4's own
 * selectors if the user navigates back there, instead of silently falling back to the AWEI/NDWI
 * defaults.
 * **Called by:
 * @see {ProcessingIntertidalPage} - Intertidal's result page, via its own export handler.
 * @memberof module:Intertidal
 * @returns {Object} A plain, JSON-serializable session snapshot.
 */
export const buildSessionExport = ({ coordinates, availableDates, waterlineLayers, evaluatedEE }) => {
  const satelliteIndex = Number.parseInt(window.sessionStorage.getItem('selectedSatelliteIndex'), 10);
  return {
    version: SESSION_EXPORT_VERSION,
    module: SESSION_EXPORT_MODULE,
    exportedAt: new Date().toISOString(),
    satelliteIndex,
    satelliteType: window.sessionStorage.getItem('typeSatellite') || 'TOA',
    includeLandsat7: satelliteIndex === 1,
    coordinates,
    availableDates,
    aoiFileName: window.localStorage.getItem('AOIIntertidalFileName') || null,
    periodData: JSON.parse(window.localStorage.getItem('intertidalPeriodData') || 'null'),
    tideData: JSON.parse(window.localStorage.getItem('tideData') || 'null'),
    tideFileName: window.localStorage.getItem('tideFileName') || null,
    timezone: window.localStorage.getItem('timezone') || '0',
    selectedMission: JSON.parse(window.sessionStorage.getItem('selectedMission') || 'null'),
    zoneIndex: OverlayHelper.zoneIndex || 'awei',
    waterlineIndex: OverlayHelper.waterlineIndex || 'ndwi',
    waterlineFeatures: evaluatedEE?.features || [],
    waterlineLayerMeta: (waterlineLayers || []).map((layer) => ({
      date: layer.date,
      color: layer.color,
      tideValue: layer.tideValue,
      visible: layer.visible,
      featureStartIndex: layer.featureStartIndex,
      featureCount: layer.featureCount,
    })),
  };
};

/**
 * @function importSessionState
 * @description Restores AOI/satellite/tide configuration (both to Redux and to the same
 * localStorage/sessionStorage keys the wizard steps and result page read directly) and stashes
 * the frozen waterline features for the result page's own mount effect to redraw — see
 * `readPendingIntertidalSession`.
 * @param {Function} dispatch - Redux dispatch.
 * @param {Object} exported - A session snapshot produced by `buildSessionExport`.
 * @see {@link buildSessionExport}
 * @see {@link readPendingIntertidalSession}
 * **Called by:
 * @see {NavigationBar} - via the "import session" icon's onImport, on Intertidal wizard steps.
 * @memberof module:Intertidal
 * @returns {void}
 */
export const importSessionState = async (dispatch, exported) => {
  await ensureEarthEngineReady();
  dispatch(Acquisition.reset());
  const {
    satelliteIndex,
    satelliteType,
    includeLandsat7,
    coordinates,
    availableDates,
    aoiFileName,
    periodData,
    tideData,
    tideFileName,
    timezone,
    selectedMission,
    zoneIndex,
    waterlineIndex,
    waterlineFeatures,
    waterlineLayerMeta,
  } = exported;

  dispatch(Acquisition.setSatellite(satelliteIndex, satelliteType, includeLandsat7));

  OverlayHelper.zoneIndex = zoneIndex || 'awei';
  OverlayHelper.waterlineIndex = waterlineIndex || 'ndwi';

  window.localStorage.setItem('AOIIntertidal', JSON.stringify(coordinates));
  if (aoiFileName) window.localStorage.setItem('AOIIntertidalFileName', aoiFileName);
  if (periodData) window.localStorage.setItem('intertidalPeriodData', JSON.stringify(periodData));
  if (tideData) window.localStorage.setItem('tideData', JSON.stringify(tideData));
  if (tideFileName) window.localStorage.setItem('tideFileName', tideFileName);
  window.localStorage.setItem('timezone', timezone || '0');
  if (selectedMission) window.sessionStorage.setItem('selectedMission', JSON.stringify(selectedMission));

  const geometry = aoiGeometry(coordinates);
  dispatch(Acquisition.setAOI(null, coordinates, geometry, null));
  dispatch(Acquisition.setAvailableDates(availableDates));

  window.sessionStorage.setItem(
    PENDING_INTERTIDAL_STORAGE_KEY,
    JSON.stringify({ waterlineFeatures, waterlineLayerMeta }),
  );
};

/**
 * @function readPendingIntertidalSession
 * @description Reads and clears the waterline data `importSessionState` left waiting (see
 * `PENDING_INTERTIDAL_STORAGE_KEY`'s comment), if any. A no-op read (returns `null`) if nothing's
 * pending, so it's safe to call unconditionally.
 * @see {@link importSessionState}
 * **Called by:
 * @see {ProcessingIntertidalPage} - Intertidal's result page, once its map has mounted.
 * @memberof module:Intertidal
 * @returns {Object|null} `{ waterlineFeatures, waterlineLayerMeta }`, or `null`.
 */
export const readPendingIntertidalSession = () => {
  const raw = window.sessionStorage.getItem(PENDING_INTERTIDAL_STORAGE_KEY);
  if (!raw) return null;
  window.sessionStorage.removeItem(PENDING_INTERTIDAL_STORAGE_KEY);
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};
